// system-key-facts.js — the STRUCTURAL Key Facts of route/dest/hotel detail
// pages, built from SYSTEM DATA (2026-07-08, owner rule consistent with the
// generator-grounding wave): departure/arrival landing sites, flight time and
// price always come from the system and OVERRIDE any same-topic generated
// keyFacts. One shared builder for BOTH renderers (SSR [...slug].astro + the
// React SEO pages) — no second truth.
//
// Incident that motivated this: the antiparos-athens Key Facts showed
// "Arrival options: Athens International Airport or Hotel Four Seasons Palace
// helipad" as generated prose and "Flight time: Varies by aircraft type" —
// while the SAME page's header already showed the real "~32 min".
//
// Sources (all read-only, all pre-existing):
//   pads        → servedPadsForCity (content-grounding.js — the canonical
//                 served gate, same as the prompt LOCATION FACTS block)
//   flight time → route.flightMin manual value first (the page-header source,
//                 routeFlightMin semantics), else the per-aircraft
//                 calcCityFlightMin spread (dest-resolvers.js)
//   price       → routeFromPriceMin over cardFromPrice (fleet-card-price.js)
import { servedPadsForCity } from './content-grounding.js';
import { routeFromPriceMin, routeCardFromLabel, isStartingPriceKeyFact } from './fleet-card-price.js';
import { calcCityFlightMin } from './dest-resolvers.js';

// Display label for a landing site: airports read as their official name;
// helipads keep their exact locations-doc name and get a " (helipad)" type
// marker ONLY when the name itself does not already say helipad/heliport —
// never a rewording ("Antiparos - Helipad" stays exactly that).
export function padDisplayLabel(p) {
  if (!p || !p.name) return '';
  if (p.type === 'airport') return p.name;
  return /helipad|heliport/i.test(p.name) ? p.name : p.name + ' (helipad)';
}

// A generated keyFact whose topic the system now owns (landing sites, flight
// time/duration, price). Superset of the price-row matcher the earlier waves
// used — matching is on the LABEL only.
export function isStructuralKeyFact(label) {
  if (typeof label !== 'string') return false;
  const l = label.trim().toLowerCase();
  return isStartingPriceKeyFact(l)
    || /depart|arriv|landing|helipad|heliport|flight time|flight duration|^duration$/.test(l);
}

// Flight-time indication from the SAME sources the page header uses: the
// manual route.flightMin wins (header truth); otherwise the per-aircraft
// engine spread ("≈ 32–52 min depending on aircraft"); no system value at
// all → "On request" (never invented).
export function flightTimeFactValue({ from, to, flightMin, fleet, locations }) {
  const manual = Number(flightMin);
  if (Number.isFinite(manual) && manual > 0) return '≈ ' + Math.round(manual) + ' min';
  const times = (fleet || [])
    .map(f => calcCityFlightMin(locations, fleet, from, to, f.id))
    .filter(t => Number.isFinite(t) && t > 0);
  if (!times.length) return 'On request';
  const lo = Math.min(...times);
  const hi = Math.max(...times);
  return lo === hi
    ? '≈ ' + lo + ' min'
    : '≈ ' + lo + '–' + hi + ' min depending on aircraft';
}

const priceFactValue = (min) => (Number.isFinite(Number(min)) && Number(min) > 0)
  ? routeCardFromLabel(min)   // "from EUR X" (de-DE), the canonical label
  : 'On request';

// ROUTE: Departure / Arrival options / Flight time / Price.
// Facts are { label, items } — items.length > 1 renders as a bullet list.
export function buildRouteSystemKeyFacts(route, { fleet = [], locations = [], operators = [] } = {}) {
  if (!route) return [];
  const facts = [];
  const dep = servedPadsForCity(route.from, locations, operators).map(padDisplayLabel).filter(Boolean);
  const arr = servedPadsForCity(route.to, locations, operators).map(padDisplayLabel).filter(Boolean);
  if (dep.length) facts.push({ label: 'Departure', items: dep });
  if (arr.length) facts.push({ label: 'Arrival options', items: arr });
  facts.push({ label: 'Flight time', items: [flightTimeFactValue({ from: route.from, to: route.to, flightMin: route.flightMin, fleet, locations })] });
  facts.push({ label: 'Price', items: [priceFactValue(routeFromPriceMin(route.cardFromPrice))] });
  return facts;
}

// DESTINATION: Landing options / Flight time from Athens / Price (the
// from-Athens minimum the caller already resolves via getDestFromAthensMin /
// routesFromPriceMin — same canonical chain as every other dest price surface).
export function buildDestSystemKeyFacts(dest, { fleet = [], locations = [], operators = [], fromAthensMin = null } = {}) {
  if (!dest || !dest.name) return [];
  const facts = [];
  const pads = servedPadsForCity(dest.name, locations, operators).map(padDisplayLabel).filter(Boolean);
  if (pads.length) facts.push({ label: 'Landing options', items: pads });
  const manualMin = parseInt(dest.time) || 0;
  const timeValue = dest.time === 'Hub'
    ? 'Hub'
    : flightTimeFactValue({ from: 'Athens', to: dest.name, flightMin: manualMin, fleet, locations });
  facts.push({ label: 'Flight time from Athens', items: [timeValue] });
  facts.push({ label: 'Price', items: [priceFactValue(fromAthensMin)] });
  return facts;
}

// HOTEL: Landing options for the hotel city (+ flight time from Athens when
// the hotel is not in Athens itself). No price fact — hotels have no single
// canonical from-price surface here.
export function buildHotelSystemKeyFacts(hotel, { fleet = [], locations = [], operators = [] } = {}) {
  if (!hotel || !hotel.city) return [];
  const facts = [];
  const pads = servedPadsForCity(hotel.city, locations, operators).map(padDisplayLabel).filter(Boolean);
  if (pads.length) facts.push({ label: 'Landing options', items: pads });
  if (hotel.city !== 'Athens') {
    const t = flightTimeFactValue({ from: 'Athens', to: hotel.city, flightMin: 0, fleet, locations });
    if (t !== 'On request') facts.push({ label: 'Flight time from Athens', items: [t] });
  }
  return facts;
}

// System facts FIRST, then the soft generated keyFacts (booking hints, season
// notes, …) with every structural-topic duplicate dropped — the render-time
// neutralisation of legacy generated rows like "Departure: Antiparos Airport".
// Generated values arrive ∞-sanitized upstream (seo-price-sanitizer).
export function mergeKeyFacts(systemFacts, generatedKeyFacts) {
  const soft = (generatedKeyFacts || [])
    .filter(k => k && k.label && k.value && !isStructuralKeyFact(k.label))
    .map(k => ({ label: k.label, items: [k.value] }));
  return [...(systemFacts || []), ...soft];
}
